import { HEAT_EVENT_ORDER, HEAT_PCTL, HEAT_STEPS, HEAT_WEIGHTS, HeatBand, HeatEventKey, PINK_MAJORITY } from 'constants/home';
import { HeatCounts, HeatWeek } from 'types/interfaces/activity';
import { formatWeekOf } from 'utils/isoWeek';

// Heat strip scoring (Asana 1218957945501621, Heat Strip Rubric). Pure. The
// backend has already applied the per-user weekly caps; this file applies the
// weights, the hue rule and the intensity steps, for both rows with one engine.

export interface WeekScore {
  score: number;
  pink: number;
  purple: number;
}

export const scoreWeek = (counts: HeatCounts): WeekScore => {
  let pink = 0;
  let purple = 0;
  (Object.keys(HEAT_WEIGHTS) as HeatEventKey[]).forEach((key) => {
    const n = Math.max(counts[key] ?? 0, 0);
    const points = n * HEAT_WEIGHTS[key].weight;
    if (HEAT_WEIGHTS[key].band === 'pink') pink += points;
    else purple += points;
  });
  return { score: pink + purple, pink, purple };
};

/** Nearest-rank percentile of the scores (HEAT_PCTL = 0.9 is the 90th). 0 for an empty list. */
export const percentile = (scores: number[], pctl: number = HEAT_PCTL): number => {
  if (scores.length === 0) return 0;
  const sorted = [...scores].sort((a, b) => a - b);
  const rank = Math.min(sorted.length, Math.max(1, Math.ceil(pctl * sorted.length)));
  return sorted[rank - 1];
};

/** 0 for an empty week; otherwise 1..HEAT_STEPS.length scaled against the row's percentile week, floored at 1. */
export const stepFor = (score: number, rowPercentile: number): number => {
  if (score <= 0) return 0;
  if (rowPercentile <= 0) return 1;
  return Math.min(HEAT_STEPS.length, Math.max(1, Math.ceil((HEAT_STEPS.length * score) / rowPercentile)));
};

/** Pink when pink-band points are at least PINK_MAJORITY of the score. */
export const hueFor = (pink: number, score: number): HeatBand => (score > 0 && pink >= PINK_MAJORITY * score ? 'pink' : 'purple');

export type CellKind = 'preJoin' | 'empty' | 'filled';

export interface HeatCell {
  isoWeek: string;
  kind: CellKind;
  hue: HeatBand;
  /** 0 for empty or pre-join cells */
  step: number;
  alpha: number;
  isCurrent: boolean;
  score: number;
  counts: HeatCounts;
}

export interface BuildRowOptions {
  joinedIsoWeek: string;
  currentIsoWeek: string;
  /** The You row dashes weeks before the user joined; the community row never does */
  isYou: boolean;
}

export const buildHeatRow = (weeks: HeatWeek[], options: BuildRowOptions): HeatCell[] => {
  const scored = weeks.map((week) => ({ week, ...scoreWeek(week.counts) }));
  const rowPercentile = percentile(scored.map((s) => s.score));
  return scored.map(({ week, score, pink }) => {
    const isCurrent = week.isoWeek === options.currentIsoWeek;
    const preJoin = options.isYou && week.isoWeek < options.joinedIsoWeek;
    const step = preJoin ? 0 : stepFor(score, rowPercentile);
    const kind: CellKind = preJoin ? 'preJoin' : step === 0 ? 'empty' : 'filled';
    return {
      isoWeek: week.isoWeek,
      kind,
      hue: hueFor(pink, score),
      step,
      alpha: step > 0 ? HEAT_STEPS[step - 1] : 0,
      isCurrent,
      score,
      counts: week.counts,
    };
  });
};

/** Consecutive pink weeks ending at the last cell (the current week). */
export const pinkStreak = (cells: HeatCell[]): number => {
  let n = 0;
  for (let i = cells.length - 1; i >= 0; i -= 1) {
    if (cells[i].kind === 'filled' && cells[i].hue === 'pink') n += 1;
    else break;
  }
  return n;
};

/** Caption clause after "Last 12 weeks · this week outlined": joined this week, or a streak; null for neither. */
export const captionClause = (youCells: HeatCell[], joinedIsoWeek: string, currentIsoWeek: string): string | null => {
  if (joinedIsoWeek === currentIsoWeek) return 'you joined this week';
  const streak = pinkStreak(youCells);
  if (streak >= 2) return `${streak} trading weeks in a row`;
  return null;
};

/** "Week of Sep 21 · 3 logins · 2 want adds · 9": counts first (non-zero only), score last. */
export const cellTooltip = (cell: HeatCell): string => {
  const parts = HEAT_EVENT_ORDER.filter((key) => (cell.counts[key] ?? 0) > 0).map((key) => `${cell.counts[key]} ${HEAT_WEIGHTS[key].label}`);
  const counts = parts.length > 0 ? parts.join(' · ') : 'no activity';
  return `Week of ${formatWeekOf(cell.isoWeek)} · ${counts} · ${cell.score}`;
};
